import { Button } from '@genesis/ui';
import { createFileRoute } from '@tanstack/react-router';

import { Title } from '@/common/components/elements/title';
import { EmptyState } from '@/common/components/features/empty-state';
import { ProjectCard, ProjectCardSkeleton } from '@/common/components/features/project-card';
import { useGetExploreProjectsQuery } from '@/modules/explore/queries/use-get-explore-projects-query';
import { useRequestProjectAccessMutation } from '@/modules/projects/mutations/useRequestProjectAccessMutation';

const skeletonArray = Array.from({ length: 6 });

const Explore = () => {
  const { mutate: requestProjectAccess } = useRequestProjectAccessMutation();
  const { data: projects = [], isLoading, error, refetch } = useGetExploreProjectsQuery();

  return (
    <div className='flex flex-col max-h-sidebar-h overflow-y-auto w-full p-5 mb:p-10'>
      <div className='flex flex-col w-full h-full gap-6'>
        <div className='flex flex-col gap-3'>
          <Title>Explorar em Globo</Title>
          <span className='flex items-center'>
            <span className='h-px flex-1 bg-border-neutral'></span>
          </span>
        </div>
        {error && (
          <EmptyState
            image={{
              src: '/images/empty-image.webp',
              alt: 'Ilustração representando erro ao carregar projetos - pessoas colaborando em torno de uma lâmpada quebrada',
            }}
            title='Erro ao explorar os projetos'
            description='Ocorreu um problema ao buscar os projetos. Verifique sua conexão e tente novamente.'
            actions={
              <Button
                size='large'
                variant='primary'
                onClick={() => refetch()}
                className='px-8 py-3 text-base rounded-xl transition-transform duration-200 hover:scale-[1.02]'
              >
                Tentar novamente
              </Button>
            }
          />
        )}
        {!error && !isLoading && projects.length === 0 && (
          <EmptyState
            image={{
              src: '/images/empty-image.webp',
              alt: 'Ilustração representando que não há projetos disponíveis - pessoas colaborando em torno de uma lâmpada quebrada',
            }}
            title='Nenhum projeto disponível'
            description='Não há projetos disponíveis no momento. Tente novamente mais tarde.'
          />
        )}
        <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-5 auto-rows-fr'>
          {isLoading
            ? skeletonArray.map((_, i) => <ProjectCardSkeleton key={i} style='half' />)
            : !error &&
              projects.map((project) => (
                <ProjectCard
                  key={project.id}
                  title={project.name}
                  className='h-[250px]'
                  description={project.description}
                  classNameDescription='line-clamp-2'
                  banner={{ style: 'half', alt: project.name, src: project.thumbnail }}
                  action={
                    <Button
                      variant='secondary'
                      disabled={project.requested}
                      className='w-28 h-8 rounded-100 text-sm p-0'
                      onClick={() => requestProjectAccess(project.id)}
                    >
                      {project.requested ? 'Solicitado' : 'Pedir acesso'}
                    </Button>
                  }
                />
              ))}
        </div>
      </div>
    </div>
  );
};

export const Route = createFileRoute('/_app/_dashboard/explorar')({
  head: () => ({
    meta: [
      {
        title: 'Explorar | Podcast AI',
      },
    ],
  }),
  component: Explore,
});
